import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Landmark } from "lucide-react";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/content";

const VALUES = [
  {
    icon: Landmark,
    title: "Mission",
    text: "To build enduring businesses across sectors — from craftsmanship to capital — delivering quality, trust and long-term value to the communities and markets we serve.",
  },
  {
    icon: Eye,
    title: "Vision",
    text: "To carry a near-century legacy into the future as a dynamic, diversified group — leading with innovation and excellence that defines modern India.",
  },
  {
    icon: Compass,
    title: "Values",
    text: "Generational perseverance, integrity in every venture, respect for local craftsmanship, and disciplined stewardship of what generations have built.",
  },
];

export default function About() {
  usePageMeta(
    "About — SKN Group of Companies | Heritage Since 1928",
    "The story of SKN Group: founded 1928 near Howrah Bridge, Kolkata, inspired by the Swadeshi movement. SKNBM trademark 1942, diversified across six divisions."
  );

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">About SKN Group</p>
            <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              Generations of <span className="italic bronze">perseverance</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="pb-24 md:pb-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <figure className="overflow-hidden md:sticky md:top-28">
                <img
                  src={IMAGES.origin}
                  alt="The Hooghly river near Howrah Bridge, Kolkata"
                  className="w-full h-[480px] object-cover img-grade"
                />
                <figcaption className="mt-4 text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Where it began — the banks of the Hooghly, Kolkata
                </figcaption>
              </figure>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-8 text-base md:text-lg leading-relaxed text-muted-foreground">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-foreground mb-6">
                1928 — A Swadeshi beginning
              </h2>
              <p>
                Our journey began in 1928 as a modest venture near Kolkata's iconic
                Howrah Bridge, inspired by the spirit of the Swadeshi movement. Amid
                the uncertainties of pre-independence India, a young man's search for
                livelihood became intertwined with the nation's own pursuit of
                self-reliance — promoting local craftsmanship and economic independence.
              </p>
            </Reveal>
            <Reveal>
              <p>
                Since then, we've steadily grown across key sectors — registering the
                SKNBM trademark in 1942 and becoming a private limited company in
                1968. Venturing into the carpet industry in 1977 marked a defining
                chapter, one that expanded our vision and set the course for future
                endeavours.
              </p>
            </Reveal>
            <Reveal>
              <p>
                Building on the success of our footwear and leathercraft venture,
                launched in 1989 through associate company Titas Footwear, we
                strategically expanded into retail by 2007, strengthening our presence
                across consumer markets. Our growth continued into real estate in 2001
                and the perfume and cosmetics segment in 2019. In 2022, we launched
                Youvora.com, a dedicated luxury perfume platform.
              </p>
            </Reveal>
            <Reveal>
              <p>
                From rugs and carpets to cosmetics, footwear, and real estate, we've
                grown with clarity of vision and sectoral strength. These pursuits have
                become pillars in a journey defined by generations of perseverance.
                Today, the SKN Group of Companies stands as a dynamic, diversified
                group with a focus on innovation and long-term value — more than
                9,000 people strong, with a presence across eight states of India.
              </p>
            </Reveal>
            <Reveal>
              <Link
                to="/journey"
                data-testid="about-journey-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground border-b border-foreground pb-1 hover:text-[#B8997A] hover:border-[#B8997A] transition-colors duration-300"
              >
                Explore the Full Timeline <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="ink-section py-24 md:py-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">What Guides Us</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">
              Mission, Vision <span className="italic bronze">&amp; Values</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F7F5F0]/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12} className="bg-[#0B0F14]">
                <div className="p-8 md:p-10 h-full border-t-2 border-[#B8997A]/50">
                  <v.icon size={26} strokeWidth={1.25} className="bronze" />
                  <h3 className="mt-6 font-display text-2xl md:text-3xl font-medium tracking-tight">{v.title}</h3>
                  <p className="mt-4 text-sm md:text-base text-[#F7F5F0]/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
              Six divisions. <span className="italic bronze">One standard.</span>
            </h2>
            <Link
              to="/divisions"
              data-testid="about-divisions-cta"
              className="mt-10 inline-flex items-center gap-2 bg-foreground text-background px-8 py-4 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors duration-300"
            >
              Explore Our Divisions <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
